import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import QRCode from 'qrcode';
import DataTable from '../../../components/design/DataTable';
import LoadingState from '../../../components/design/LoadingState';
import EmptyState from '../../../components/design/EmptyState';
import operationsService from '../../../services/operationsService';

function fmtDate(v) {
  if (!v) return '—';
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

export default function SmsGatewaysPage() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [hasOtherCompanies, setHasOtherCompanies] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [pairOpen, setPairOpen] = useState(false);
  const [pairCode, setPairCode] = useState('');
  const [pairQrData, setPairQrData] = useState('');
  const [pairQrUrl, setPairQrUrl] = useState('');
  const [pairExpiresIn, setPairExpiresIn] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const resp = await operationsService.listSmsAndroidGateways();
      const list = resp?.data?.gateways || resp?.gateways || [];
      const hasOthers = Boolean(resp?.data?.hasOtherCompanies);
      const cid = resp?.data?.companyId ?? null;
      setRows(Array.isArray(list) ? list : []);
      setHasOtherCompanies(hasOthers);
      setCompanyId(cid);
    } catch (e) {
      setRows([]);
      setHasOtherCompanies(false);
      setCompanyId(null);
      toast.error(e?.message || 'Falha ao carregar dispositivos');
    } finally {
      setLoading(false);
    }
  };

  const startPairing = async () => {
    setLoading(true);
    try {
      const resp = await operationsService.startSmsAndroidPairing();
      const data = resp?.data?.data || resp?.data || {};
      const code = data?.code?.toString() || '';
      const qrData = data?.qrData?.toString() || '';
      const expiresInSec = data?.expiresInSec ?? null;
      if (!code || !qrData) {
        toast.error('Falha ao gerar QR');
        return;
      }
      const url = await QRCode.toDataURL(qrData, { margin: 1, width: 280 }).catch(() => '');
      setPairCode(code);
      setPairQrData(qrData);
      setPairQrUrl(url);
      setPairExpiresIn(expiresInSec);
      setPairOpen(true);
    } catch (e) {
      toast.error(e?.message || 'Falha ao iniciar pareamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const toggleEnabled = async (gateway) => {
    const id = gateway?.id;
    if (!id) return;
    const isDisabled = String(gateway?.status || '').toLowerCase() === 'disabled';
    setLoading(true);
    try {
      await operationsService.patchSmsAndroidGateway(id, { enabled: isDisabled });
      await load();
      toast.success(isDisabled ? 'Gateway habilitado' : 'Gateway desabilitado');
    } catch (e) {
      toast.error(e?.message || 'Falha ao atualizar gateway');
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => ([
    { key: 'name', label: 'Gateway' },
    { key: 'deviceId', label: 'Device ID' },
    {
      key: 'messageCategory',
      label: 'Tipo',
      render: (v) => v === 'billing' ? 'Cobrança' : v === 'otp' ? 'OTP' : 'Serviço'
    },
    {
      key: 'status',
      label: 'Status',
      render: (_, row) => {
        const disabled = String(row?.status || '').toLowerCase() === 'disabled';
        if (disabled) return 'Desativado';
        const ls = row?.lastSeenAt ? new Date(row.lastSeenAt).getTime() : null;
        const online = ls ? (Date.now() - ls) <= 70_000 : false;
        return online ? 'Online' : 'Offline';
      }
    },
    { key: 'appVersion', label: 'Versão' },
    { key: 'batteryPct', label: 'Bateria', align: 'right', render: (v) => (v === null || v === undefined) ? '—' : `${v}%` },
    { key: 'signalDbm', label: 'Sinal', align: 'right', render: (v) => (v === null || v === undefined) ? '—' : `${v} dBm` },
    { key: 'lastSeenAt', label: 'Último heartbeat', render: (v) => fmtDate(v) },
    {
      key: '_actions',
      label: 'Ações',
      render: (_, row) => {
        const disabled = String(row?.status || '').toLowerCase() === 'disabled';
        return (
          <Stack direction="row" spacing={1}>
            <TextField
              select
              size="small"
              value={row?.messageCategory || 'service'}
              onChange={async (e) => {
                setLoading(true);
                try {
                  await operationsService.patchSmsAndroidGateway(row.id, { messageCategory: e.target.value });
                  await load();
                  toast.success('Tipo atualizado');
                } catch (error) {
                  toast.error(error?.message || 'Falha ao atualizar tipo');
                } finally {
                  setLoading(false);
                }
              }}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="billing">Cobrança</MenuItem>
              <MenuItem value="otp">OTP</MenuItem>
              <MenuItem value="service">Serviço</MenuItem>
            </TextField>
            <Button
              size="small"
              variant="outlined"
              color={disabled ? 'success' : 'warning'}
              onClick={() => toggleEnabled(row)}
              disabled={loading}
            >
              {disabled ? 'Habilitar' : 'Desabilitar'}
            </Button>
          </Stack>
        );
      }
    }
  ]), [loading]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">Dispositivos SMS (Android)</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => startPairing()} disabled={loading}>Conectar via QR</Button>
          <Button variant="outlined" onClick={() => load()} disabled={loading}>Atualizar</Button>
        </Stack>
      </Stack>

      {loading && <LoadingState label="Carregando..." />}

      {!loading && rows.length === 0 && (
        <Paper sx={{ p: 2 }}>
          <EmptyState
            title="Nenhum dispositivo"
            subtitle={
              hasOtherCompanies
                ? `Não encontrei gateways na sua empresa ${companyId ?? '—'}. Existe gateway cadastrado em outra empresa. Confirme se o pareamento (QR) foi feito no mesmo login/empresa.`
                : `Quando o app Gateway registrar/heartbeat, ele aparecerá aqui. Empresa atual: ${companyId ?? '—'}`
            }
          />
        </Paper>
      )}

      {!loading && rows.length > 0 && (
        <DataTable columns={columns} rows={rows} getKey={(r) => r.id} />
      )}

      <Dialog open={pairOpen} onClose={() => setPairOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Conectar dispositivo</DialogTitle>
        <DialogContent>
          <Stack spacing={1} alignItems="center" sx={{ mt: 1 }}>
            {pairQrUrl ? (
              <img src={pairQrUrl} alt="QR" style={{ width: 280, height: 280 }} />
            ) : (
              <Paper sx={{ p: 2 }}>Gerando QR…</Paper>
            )}
            <Typography variant="body2" color="text.secondary">
              Abra o app do Gateway, escaneie o QR e inicie o gateway.
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Código
            </Typography>
            <Paper sx={{ p: 1.25, width: '100%', textAlign: 'center' }}>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{pairCode}</Typography>
            </Paper>
            {pairExpiresIn != null && (
              <Typography variant="caption" color="text.secondary">
                Expira em {pairExpiresIn}s
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              navigator.clipboard?.writeText?.(pairQrData).catch(() => {});
              toast.success('QR copiado');
            }}
            disabled={!pairQrData}
          >
            Copiar QR
          </Button>
          <Button onClick={() => setPairOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
